/**
 * @prettier
 */

// React Packages
import { useTranslation } from 'react-i18next';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
// Components
import Header from '../../components/Header';
import CustomButton from '../../components/CustomButton';
import SubmissionFlowStepper from '../../components/SubmissionFlowStepper';
import Navigator from '../../components/Navigator';
import StageComplete from '../../components/Messages/StageComplete';
import PreviewState from './components/PreviewState';
import PrepareToUploadState from './components/PrepareToUploadState';
import ErrorCaptureState from './components/ErrorCaptureState';
import ActiveModalOverlayState from './components/ActiveModalOverlayState';
import DocumentSelectionState from './components/DocumentSelectionState';

// Services
import { bytesToSize } from '../../services/shared/helpers';
import ApiService from '../../services/api/api';
import pdfCreationService from '../../services/shared/pdfCreationService';
import { PDFDocument } from 'pdf-lib';

// Actions
import {
    completeStage,
    setIsPoaUploadInProgress,
} from '../../state/actions/submissionStateActions';
import {
    fetchPoaDocumentStatus,
    setDocumentUploaded,
    setPoaCompleted,
    setPoaLoading,
} from '../../state/actions/poaDocumentsActions';

// Images
import poaModalImage from '../../assets/img/proof_address_modal.jpg';

// Config
import DataDogService from '../../services/shared/datadogService';
import logService from '../../services/shared/logService';
import { LoadingStateAnimation } from '../../components/ProcessingLayout';

// Styles
import '../../styles/pages/new_ui/_proof_of_address_page.scss';

const ProofOfAddressPage = ({
    poaCompleted,
    completeStage,
    poaDocuments,
    additionalDocuments,
    isDocSubmitted,
    docCompleted,
    isPoaUploadInProgress,
    branding,
    fetchPoaDocumentStatus,
    setDocumentUploaded,
    setPoaCompleted,
    setPoaLoading,
    isPoaLoading,
    poaDocumentsState,
    setIsPoaUploadInProgress,
}) => {
    const { t } = useTranslation();
    const [poaImage, setPoaImage] = useState(null);
    const [mimeType, setMimeType] = useState(null);
    const [dimensions, setDimensions] = useState({});
    const [isInvalidFileType, setIsInvalidFileType] = useState(false);
    const [isInvalidFileSize, setIsInvalidFileSize] = useState(false);
    const [isCaptureError, setIsCaptureError] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [showStageComplete, setShowStageComplete] = useState(false);
    const [errorRetryCount, setErrorRetryCount] = useState(3);
    const [isRetryLimitReached, setIsRetryLimitReached] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [showDocumentSelection, setShowDocumentSelection] = useState(true);
    const [currentPoaUploadKey, setCurrentPoaUploadKey] = useState(null);
    const [navigation, setNavigation] = useState({
        action: 'load',
        props: null,
    });

    const brandingColor = `#${branding.data.company_branding.primary_color}`;
    const allowedFileTypes = ['image/jpeg', 'image/png', 'application/pdf'];
    const pdfFileType = 'blob';
    const allowedUnits = ['Bytes', 'KB'];
    const maxFileSize = 10;

    const primaryFocusRef = useRef();

    // Draw captured image to canvas to retain image orientation.
    const drawToCanvas = image => {
        const hiddenCanvas = document.querySelector('#poaCanvas');
        hiddenCanvas.width = image.width;
        hiddenCanvas.height = image.height;

        const ctx = hiddenCanvas.getContext('2d');
        ctx.mozImageSmoothingEnabled = false;
        ctx.webkitImageSmoothingEnabled = false;
        ctx.msImageSmoothingEnabled = false;
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(image, 0, 0, image.width, image.height);

        const canvasImage = hiddenCanvas.toDataURL('image/jpeg');
        return canvasImage;
    };

    const handleStartCamera = () => {
        document.getElementById('capturePOA').click();
        document
            .getElementById('capturePOA')
            .addEventListener('change', event => handleCameraCapture(event));
    };

    const handleCameraCapture = event => {
        const poaInput = document.getElementById('capturePOA');
        resetValidation();
        if (poaInput.files && poaInput.files[0]) {
            try {
                // First get the mimeType for creating pdf, will always be jpg as it is drawn to the canvas.
                setMimeType('jpg');

                const fileReader = new FileReader();
                fileReader.readAsDataURL(poaInput.files[0]);
                fileReader.addEventListener('load', function (event) {
                    const image = new Image();
                    image.src = event.target.result;
                    image.onload = function () {
                        setPoaImage(drawToCanvas(this));
                        setShowPreview(true);
                        // Set the dimensions for the pdf
                        setDimensions({
                            width: this.width,
                            height: this.height,
                        });
                    };
                });
                // Reset the input value
                poaInput.value = null;
            } catch (error) {
                poaInput.value = null;
                setIsCaptureError(true);
                DataDogService.createError(error);
                logService.error(error);
            }
        } else {
            // Reset the input value
            poaInput.value = null;
        }
    };
    // Convert file to pdf
    const convertImageToPDF = async (file, mimeType, dimensions) => {
        try {
            const { generatePdfFromImages } = pdfCreationService();

            const pdf = await generatePdfFromImages(
                file,
                mimeType,
                pdfFileType,
                dimensions
            );
            return pdf;
        } catch (error) {
            throw error;
        }
    };
    // Submit the proof of address document to the backend
    const postPoaFile = (file, uploadKey) => {
        // Using the dedicated loading action for better separation of concerns
        setPoaLoading(true);

        // clearError(this); // NOSONAR
        ApiService.uploadPoa(file, uploadKey)
            .then(response => {
                // Log success response to datadog
                DataDogService.log(
                    'Successfully send Proof of Address document.'
                );

                // Check document status after successful upload
                checkRequiredDocumentsStatus();
            })
            .catch(error => {
                // Handle errors
                DataDogService.createError(
                    'Unable to send Proof of Address document.'
                );
                logService.error(error);

                // Reset loading state on error
                setPoaLoading(false);

                // Navigation to error view
                setNavigation({
                    action: 'error',
                    props: {
                        retryAction: () =>
                            postPoaFile(file, currentPoaUploadKey),
                    },
                });
            });
    };

    // Submit the image after preview
    const handleSubmitCapturedPdf = async () => {
        if (poaImage && mimeType) {
            try {
                setPoaLoading(true);
                const pdf = await convertImageToPDF(
                    poaImage,
                    mimeType,
                    dimensions
                );
                if (!pdf) {
                    throw new Error('PDF conversion returned empty result');
                }
                postPoaFile(pdf, currentPoaUploadKey);
            } catch (error) {
                setPoaLoading(false);
                setIsCaptureError(true);
                logService.error('Error converting image to PDF', error);
                DataDogService.createError('Error converting image to PDF');
            }
        } else {
            DataDogService.createError(
                'Error uploading proof of address file, no image captured'
            );
        }
    };

    // Handle document selection
    const handleDocumentSelect = document => {
        setIsPoaUploadInProgress(true);
        // If document has child, navigate to its child
        if (document.child && document.child.length > 0) {
            // Add parent reference for back navigation
            const documentWithParent = {
                ...document,
                parent: selectedDocument, // Keep reference to parent
            };
            setSelectedDocument(documentWithParent);
        } else {
            // If leaf document, proceed to capture/upload
            // Add parent reference for back navigation
            const documentWithParent = {
                ...document,
                parent: selectedDocument, // Keep reference to parent
            };
            setSelectedDocument(documentWithParent);
            setShowDocumentSelection(false);
            setCurrentPoaUploadKey(document.poa_upload_key);
        }
    };

    // Handle back button
    const handleGoBack = () => {
        if (selectedDocument) {
            // If we're in document upload/capture view
            if (!showDocumentSelection) {
                // When returning from upload state, maintain the document context
                setShowDocumentSelection(true);
                resetCaptureState();
            }
            // If we're in document selection view with a parent
            if (selectedDocument.parent) {
                // If there's a parent, go back to parent's view
                const parent = selectedDocument.parent;
                if (parent === selectedDocument) {
                    // Safety check against circular reference
                    setSelectedDocument(null);
                } else {
                    setSelectedDocument(parent);
                }
            }
            // If we're at the root level
            else {
                // If no parent, go back to main selection
                setSelectedDocument(null);
            }
        }
    };

    // Reset for new capture attempt
    const resetCaptureState = () => {
        setPoaImage(null);
        setShowPreview(false);
        setIsCaptureError(false);
    };

    // Retry capture/upload error
    const handleCaptureErrorRetry = () => {
        document
            .getElementById('capturePOA')
            .removeEventListener('change', handleCameraCapture);
        if (errorRetryCount <= 1) {
            setIsRetryLimitReached(true);
        } else {
            setErrorRetryCount(errorRetryCount - 1);
        }
        resetCaptureState();
    };

    // Retry Capture POA
    const handleRetryCapture = () => {
        document
            .getElementById('capturePOA')
            .removeEventListener('change', handleCameraCapture);
        resetCaptureState();
    };
    /**
     *
     * File upload POA starts here
     *
     **/

    // Select file
    const handleSelectFiles = () => {
        resetValidation();
        document.getElementById('uploadPOA').click();

        // Set change handler to fire only once to prevent multiple api calls.
        document
            .getElementById('uploadPOA')
            .addEventListener('change', event => handleUploadPoaFile(event), {
                once: true,
            });
    };

    // Load image and convert to pdf
    const uploadImageAsPdf = file => {
        const mimeType = 'jpg';
        const readFileAsDataURL = file => {
            return new Promise(resolve => {
                const fileReader = new FileReader();
                fileReader.onload = event => resolve(event.target.result);
                fileReader.readAsDataURL(file);
            });
        };

        const loadImage = dataUrl => {
            return new Promise(resolve => {
                const image = new Image();
                image.onload = () => resolve(image);
                image.src = dataUrl;
            });
        };

        (async () => {
            try {
                const dataUrl = await readFileAsDataURL(file);
                const image = await loadImage(dataUrl);

                // Convert to pdf
                const dimensions = {
                    width: image.width,
                    height: image.height,
                };

                const canvasImage = drawToCanvas(image);
                const pdf = await convertImageToPDF(
                    canvasImage,
                    mimeType,
                    dimensions
                );

                // Send file
                postPoaFile(pdf, currentPoaUploadKey);
            } catch (error) {
                console.error('Error processing image file:', error);
                setIsCaptureError(true);
                DataDogService.createError('Error processing uploaded image');
            }
        })();
    };

    // Upload file and perform file validation
    const handleUploadPoaFile = event => {
        resetValidation();
        // Set loading at this stage.
        const poaInput = document.getElementById('uploadPOA');
        if (poaInput.files && poaInput.files[0]) {
            try {
                const file = poaInput.files[0];

                if (isValidFile(file)) {
                    if (file.type === 'application/pdf') {
                        // Read PDF file
                        const reader = new FileReader();
                        reader.readAsArrayBuffer(file);

                        // When reader is ready
                        reader.onload = async () => {
                            try {
                                // Load pdf into new pdf
                                const pdfDoc = await PDFDocument.load(
                                    reader.result,
                                    { ignoreEncryption: true }
                                );

                                // Save new pdf data
                                const pdfBytes = await pdfDoc.save({
                                    useObjectStreams: false,
                                });

                                // Build pdf file
                                const bytes = await new Uint8Array(pdfBytes);
                                const blob = await new Blob([bytes], {
                                    type: 'application/pdf',
                                });

                                // Send new pdf file
                                postPoaFile(blob, currentPoaUploadKey);
                            } catch (err) {
                                // If building fails, send original file
                                postPoaFile(file, currentPoaUploadKey);
                            }
                        };
                    } else {
                        uploadImageAsPdf(file);
                    }
                }

                // Set invalid file size modal
                if (!isValidFileSize(file.size)) {
                    setIsInvalidFileSize(true);
                }

                // Set invalid file type modal
                if (!isValidFileType(file.type)) {
                    setIsInvalidFileType(true);
                }
            } catch (error) {
                poaInput.value = null;
                setIsCaptureError(true);
                DataDogService.createError(error);
                logService.error(error);
            }
            poaInput.value = null;
        } else {
            poaInput.value = null;
        }
    };

    /**
     *
     * File validation starts here
     *
     **/

    // Check Valid file
    const isValidFile = file =>
        isValidFileType(file.type) && isValidFileSize(file.size);

    // Check valid file type
    const isValidFileType = fileType => allowedFileTypes.includes(fileType);

    // Check valid file size
    const isValidFileSize = fileSize => {
        const fileSizeData = bytesToSize(fileSize);

        if (allowedUnits.includes(fileSizeData.unit)) {
            return true;
        }

        if (fileSizeData.unit === 'MB' && fileSizeData.size <= maxFileSize) {
            return true;
        }

        return false;
    };

    // Check if all required documents have been uploaded - using Redux
    const checkRequiredDocumentsStatus = () => {
        fetchPoaDocumentStatus()
            .then(result => {
                // Determine if the document was from POA or additional section
                const isPoaDocument = poaDocuments.some(
                    doc =>
                        doc.poa_upload_key === currentPoaUploadKey ||
                        (doc.child &&
                            doc.child.some(
                                child =>
                                    child.poa_upload_key === currentPoaUploadKey
                            ))
                );

                // Update the Redux store with the document type
                if (currentPoaUploadKey) {
                    setDocumentUploaded(currentPoaUploadKey, isPoaDocument);
                }

                if (result && result.allRequiredUploaded) {
                    // Set POA as completed in Redux when all requirements are met
                    setPoaCompleted(true);
                    // Return to document selection with updated status
                    handleGoBack();
                } else {
                    // Set loading state to false when we have the result
                    setPoaLoading(false);
                    // Return to document selection to continue uploading
                    handleGoBack();
                }
            })
            .catch(error => {
                // Reset loading state on error
                setPoaLoading(false);
                DataDogService.createError(
                    'Failed to check proof of address documents status'
                );
            });
    };

    const processCompleteStage = () => {
        setPoaLoading(true);
        // Navigate to the next step in the flow
        setShowStageComplete(true);
        setPoaCompleted(true);
        setIsPoaUploadInProgress(false);
        // Call completeStage if it's a prop
        if (typeof completeStage === 'function') {
            completeStage('poa_upload');
        }
        // Set navigation to go to the next page
        setNavigation({
            action: 'next',
        });
    };
    const resetValidation = () => {
        setIsInvalidFileSize(false);
        setIsInvalidFileType(false);
        setShowModal(false);
    };
    // Show modal if ID is already submitted
    const showDocumentUploadModal = () => {
        if (isDocSubmitted && docCompleted && !isPoaUploadInProgress) {
            return true;
        }
        return false;
    };
    useEffect(() => {
        // Sets focus to primary heading on first render
        if (primaryFocusRef && primaryFocusRef.current) {
            primaryFocusRef.current.focus();
        }

        // If the POA is already completed from the server state, show completion
        if (poaCompleted) {
            setShowStageComplete(true);
            // Ensure the Redux state is in sync
            setPoaCompleted(true);
        } else {
            // Fetch document status on initial load
            fetchPoaDocumentStatus().then(result => {
                if (result && result.allRequiredUploaded) {
                    setPoaCompleted(true);
                } else {
                    setPoaCompleted(false);
                }
            });
        }

        // Sets document title
        document.title = 'Proof of Address - Upload';

        // Clear poaUpload State when component unmounts
        return () => {
            setIsPoaUploadInProgress(false);
        };
    }, [poaCompleted, fetchPoaDocumentStatus]);

    useEffect(() => {
        const allRequiredUploaded =
            (poaDocumentsState.documents.length === 0 ||
                poaDocumentsState.poaRequiredUploaded) &&
            (poaDocumentsState.additionalDocuments.length === 0 ||
                poaDocumentsState.additionalRequiredUploaded);

        if (poaDocumentsState.isCompleted !== allRequiredUploaded) {
            setPoaCompleted(allRequiredUploaded);
        }
    }, [
        poaDocumentsState.additionalDocuments.length,
        poaDocumentsState.additionalRequiredUploaded,
        poaDocumentsState.documents.length,
        poaDocumentsState.isCompleted,
        poaDocumentsState.poaRequiredUploaded,
        setPoaCompleted,
    ]);

    useEffect(() => {
        // Sets focus to primary heading on first render
        if (primaryFocusRef && primaryFocusRef.current) {
            primaryFocusRef.current.focus();
        }

        // Sets modal for invalid file size or invalid file type
        if (isInvalidFileSize || isInvalidFileType) {
            setShowModal(true);
        }
    }, [isInvalidFileSize, isInvalidFileType]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const memoizedHandleCameraCapture = useCallback(handleCameraCapture, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const memoizedHandleUploadPoaFile = useCallback(handleUploadPoaFile, []);

    useEffect(() => {
        // Cleanup function for event listeners
        return () => {
            // Remove all event listers
            const capturePOA = document.getElementById('capturePOA');
            const uploadPOA = document.getElementById('uploadPOA');

            if (capturePOA) {
                capturePOA.removeEventListener(
                    'change',
                    memoizedHandleCameraCapture
                );
            }

            if (uploadPOA) {
                uploadPOA.removeEventListener(
                    'change',
                    memoizedHandleUploadPoaFile
                );
            }
        };
    }, [memoizedHandleCameraCapture, memoizedHandleUploadPoaFile]);

    // Document upload is already completed
    if (showStageComplete && navigation.action === 'load') {
        return (
            <div className='root-layout-container proof-of-address-page'>
                <Header />
                <StageComplete
                    continue={() => setNavigation({ action: 'next' })}
                    message='idpal_poa_captured'
                />
                <Navigator
                    page={'proof_of_address'}
                    action={navigation.action}
                    propsToPass={navigation.props}
                />
            </div>
        );
    }

    return (
        <div className='root-layout-container proof-of-address-page'>
            <Header />
            <SubmissionFlowStepper />
            {isPoaLoading ? (
                <LoadingStateAnimation />
            ) : (
                <>
                    {showDocumentSelection && (
                        <>
                            <DocumentSelectionState
                                primaryFocusRef={primaryFocusRef}
                                documents={poaDocuments}
                                additionalDocuments={additionalDocuments}
                                selectedDocument={selectedDocument}
                                handleDocumentSelect={handleDocumentSelect}
                                handleGoBack={handleGoBack}
                                brandingColor={brandingColor}
                            />
                            {!(
                                selectedDocument &&
                                selectedDocument.child &&
                                selectedDocument.child.length > 0
                            ) && (
                                <CustomButton
                                    id='proof-of-address-continue'
                                    className='btn btn__arrow'
                                    label={t('idpal_continue')}
                                    type='submit'
                                    isDisabled={!poaDocumentsState.isCompleted}
                                    handleClick={processCompleteStage}
                                />
                            )}
                        </>
                    )}

                    {/* Show instructions state */}
                    {!showDocumentSelection &&
                        !showPreview &&
                        !isCaptureError && (
                            <PrepareToUploadState
                                primaryFocusRef={primaryFocusRef}
                                title={selectedDocument?.title || ''}
                                subTitle={selectedDocument?.sub_title || ''}
                                handleStartCamera={handleStartCamera}
                                handleSelectFiles={handleSelectFiles}
                                handleBackButton={handleGoBack}
                                brandingColor={brandingColor}
                            />
                        )}

                    {!showDocumentSelection && showPreview && (
                        <PreviewState
                            primaryFocusRef={primaryFocusRef}
                            poaImage={poaImage}
                            handleSubmitCapturedPdf={handleSubmitCapturedPdf}
                            handleRetryCapture={handleRetryCapture}
                        />
                    )}

                    {!showDocumentSelection && isCaptureError && (
                        <ErrorCaptureState
                            primaryFocusRef={primaryFocusRef}
                            handleCaptureErrorRetry={handleCaptureErrorRetry}
                            isRetryLimitReached={isRetryLimitReached}
                        />
                    )}
                </>
            )}
            {!isPoaLoading && (
                <ActiveModalOverlayState
                    showDocumentUploadModal={showDocumentUploadModal}
                    poaModalImage={poaModalImage}
                    isInvalidFileSize={isInvalidFileSize}
                    isInvalidFileType={isInvalidFileType}
                    showModal={showModal}
                />
            )}
            <input
                className='hidden'
                type='file'
                id='capturePOA'
                accept='image/*'
                capture
                onClick={handleCameraCapture}
            />
            <input
                className='hidden'
                type='file'
                id='uploadPOA'
                accept='.png,.jpg,.jpeg,.pdf'
                onClick={handleUploadPoaFile}
            />
            <canvas className='hidden' id='poaCanvas'></canvas>
            <Navigator
                page={'proof_of_address'}
                action={navigation.action}
                propsToPass={navigation.props}
            />
        </div>
    );
};

function mapStateToProps(state) {
    return {
        poaDocuments: state.config.profile.data.proof_of_address || [],
        additionalDocuments:
            state.config.profile.data.additional_documents || [],
        docCompleted:
            state.submissionState.submissionState.document_upload.completed,
        isDocSubmitted: state.config.isDocSubmitted,
        poaCompleted:
            state.submissionState.submissionState.poa_upload.completed,
        branding: state.config.profile,
        poaDocumentsState: state.poaDocuments,
        isPoaLoading: state.poaDocuments.loading,
        isPoaUploadInProgress: state.submissionState.isPoaUploadInProgress,
    };
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators(
        {
            completeStage,
            fetchPoaDocumentStatus,
            setDocumentUploaded,
            setPoaCompleted,
            setPoaLoading,
            setIsPoaUploadInProgress,
        },
        dispatch
    );
}

export default connect(mapStateToProps, mapDispatchToProps)(ProofOfAddressPage);
